import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { API_POST } from '../../services';
import { ENDPOINTS } from '../../constants/endpoints';

// ========================================
// TYPES
// ========================================
export type ShiftDecision = 'approve' | 'reject';

export interface DecisionShiftPayload {
    requestId: number;
    requestSeqId: number;
    requestType?: string;
    requestSite?: string;
    affectedUserId?: string;
    affectedUserName?: string;
    decidedById?: string;
    decidedByName?: string;
    isGigRequest?: boolean;
    eventAction?: string[];
    rejectionNotes?: string;
}

interface DecisionShiftState {
    lastDecision: { decision: ShiftDecision; payload: DecisionShiftPayload } | null;
    loading: boolean;
    success: boolean;
    error: string | null;
}

const initialState: DecisionShiftState = {
    lastDecision: null,
    loading: false,
    success: false,
    error: null,
};

// ========================================
// ENDPOINT MAP
// ========================================
const DECISION_ENDPOINTS: Record<ShiftDecision, string> = {
    approve: ENDPOINTS.APPROVE_SHIFT,
    reject: ENDPOINTS.REJECT_SHIFT,
};

// ========================================
// ASYNC THUNK
// ========================================
export const decideShift = createAsyncThunk(
    'decisionShift/decideShift',
    async (
        { decision, payload }: { decision: ShiftDecision; payload: DecisionShiftPayload },
        { rejectWithValue }
    ) => {
        try {
            // Use camelCase to match C# backend's default JSON serialization
            const innerPayload = {
                requestId: payload.requestId,
                requestSeqId: payload.requestSeqId,
                requestType: payload.requestType,
                requestSite: payload.requestSite,
                affectedUserId: payload.affectedUserId,
                affectedUserName: payload.affectedUserName,
                ...(decision === 'approve'
                    ? { approvedById: payload.decidedById, approvedByName: payload.decidedByName }
                    : { rejectedById: payload.decidedById, rejectedByName: payload.decidedByName, rejectionNotes: payload.rejectionNotes }),
                isGigRequest: payload.isGigRequest ?? false,
                eventAction: payload.eventAction,
            };

            // Send payload directly without wrapper - backend binds from body
            const response = await API_POST(DECISION_ENDPOINTS[decision], innerPayload);
            console.log(`✅ ${decision} Shift API Response:`, response);
            return { decision, payload };
        } catch (error: any) {
            console.error(`❌ ${decision} Shift API Error:`, error);
            return rejectWithValue(error?.message || `Failed to ${decision} shift`);
        }
    }
);

// ========================================
// SLICE
// ========================================
const decisionShiftSlice = createSlice({
    name: 'decisionShift',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        resetDecisionShift: (state) => {
            state.lastDecision = null;
            state.loading = false;
            state.success = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(decideShift.pending, (state) => {
                state.loading = true;
                state.success = false;
                state.error = null;
            })
            .addCase(decideShift.fulfilled, (state, action) => {
                state.loading = false;
                state.success = true;
                state.lastDecision = action.payload;
            })
            .addCase(decideShift.rejected, (state, action) => {
                state.loading = false;
                state.success = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError, resetDecisionShift } = decisionShiftSlice.actions;
export default decisionShiftSlice.reducer;